<template>
  <ToDoContent class="to-do-details">
    <template #content>
      <Tab
        :tabs="tabs"
        :control="false"
        :metrics="tabMetrics"
        :changeCurrentTab="$route.query.comment ? 'comments' : ''"
        class="to-do-details__tabs"
      >
        <template #tab-todo>
          <ToDoItem
            :item="item"
            :showDescriptionProp="true"
            :disableCheckbox="item.owners.every(owner => owner.id !== currentUser.id)"
            @onCheckboxClick="onCheckboxClick"
            @onEdit="onEdit"
          />
        </template>

        <template #tab-files>
          <FileList
            :key="filterFiles.withDeleted"
            :files="filesResponse.content"
            :last="filesResponse.last"
            :withDeleted="filterFiles.withDeleted"
            :uploadListKey="item.id"
            :projectScoped="false"
            @deleteFile="onDeleteFile"
            @upload="onUploadFiles"
            @onScroll="onScrollFiles"
            @showDeleted="onShowDeletedFiles"
          />
        </template>

        <template #tab-comments>
          <CommentList
            :targetId="item.id"
            :targetResourceId="targetResourceId"
            :filterForComments="filterForComments"
            :innerPagging="false"
            :projectScoped="false"
            :showHistoryToggle="false"
            :referencesEnabled="false"
            :commentsResponse="commentsResponse"
            @filterUpdate="onUpdateCommentFilter"
            @commentsResponseChange="commentsResponse = $event"
          />
        </template>
      </Tab>
    </template>

    <template #footer>
      <BackButtonComponent @goBack="onBack" />
    </template>
  </ToDoContent>
</template>

<script lang="ts">
import { Options, Vue } from "vue-class-component"
import { Emit, Prop, Watch } from "vue-property-decorator"
import { Action, Getter } from "s-vuex-class"

import CommentList from "@/components/Comment/CommentList.vue"
import BackButtonComponent from "@/components/ControlButtons/BackButtonComponent.vue"
import FileList from "@/components/File/FileList.vue"
import Tab from "@/components/Tab/index.vue"
import ToDoContent from "@/components/ToDo/ToDoContent.vue"
import ToDoItem from "@/components/ToDo/ToDoItem.vue"

import {
  AttachmentFileMetadataParams,
  BlockTab,
  Comment,
  CommentTarget,
  FileMetaData,
  FilterComment,
  FilterSearch,
  PayloadCommentCount,
  ServerResponse,
  SubscriptionCommentDTO,
  UploadAttachment,
  User,
  VUFile
} from "@/models"
import { ResourceEvent, ResourceType, ServerSendAttachmentUpdate } from "@/models/common"
import { ToDo } from "@/models/ToDo"
import { Actions, Getters } from "@/store"
import { parsingServerResponseContent } from "@/utils/helpers"

/** Presents an opened ToDo across the design's ToDo, files, and comments tabs. */
@Options({
  name: "ToDoDetails",
  components: {
    BackButtonComponent,
    CommentList,
    FileList,
    Tab,
    ToDoContent,
    ToDoItem
  }
})
export default class ToDoDetails extends Vue {
  @Prop({ required: true }) readonly item: ToDo

  @Getter(Getters.GET_USER_CURRENT_PROFILE) readonly currentUser: User
  @Getter(Getters.SUBSCRIPTION_MESSAGE) readonly subscriptionMessage: (
    type: ResourceType
  ) => SubscriptionCommentDTO[] | undefined
  @Getter(Getters.SUBSCRIPTION_MESSAGE_NOTIFICATION) readonly subscriptionNotification: (
    type: ResourceEvent
  ) => ServerSendAttachmentUpdate | null

  @Action(Actions.GET_COMMENT_COUNT) getCommentCount: (
    payload: PayloadCommentCount
  ) => Promise<number | false>
  @Action(Actions.ATTACHMENT_FILE_METADATA) getFiles: (
    payload: AttachmentFileMetadataParams
  ) => Promise<ServerResponse<FileMetaData>>
  @Action(Actions.ATTACHMENT_UPLOAD_TARGET) uploadFiles: (
    payload: UploadAttachment
  ) => Promise<FileMetaData[] | false>
  @Action(Actions.TODO_ITEM_DELETE_ATTACHMENT) deleteAttachment: (payload: {
    todoItemId: string
    fileMetadataId: string
  }) => Promise<boolean>

  filterForComments = FilterComment.createDefault()
  filterFiles = FilterSearch.createDefault()
  filesResponse = new ServerResponse<FileMetaData>()
  commentsResponse = new ServerResponse<Comment>()

  get tabs(): BlockTab[] {
    return [
      { id: "todo", label: "ToDo" },
      {
        id: "files",
        label: "File",
        countOptions: { number: "files", notify: "filesNotify" }
      },
      {
        id: "comments",
        label: "Comments",
        countOptions: { number: "comments", notify: "commentsNotify" }
      }
    ]
  }

  get tabMetrics() {
    return {
      files: this.filesResponse.totalElements,
      filesNotify: 0,
      comments: this.commentCount,
      commentsNotify: 0
    }
  }

  get commentCount() {
    return this.commentsResponse.totalElements
  }

  get targetResourceId(): CommentTarget {
    return {
      id: this.item.id,
      type: "TODO_ITEM"
    }
  }

  get subscriptionComments() {
    return this.subscriptionMessage("COMMENT_CHANGE")
  }

  get subscriptionFiles() {
    return this.subscriptionNotification("FILE_METADATA_CHANGED")
  }

  created() {
    this.filterForComments.addSort("createdDate", false)
    this.filterFiles.addSort("createdDate", false)
    this.loadCommentCount()
    this.loadFiles()
  }

  async loadCommentCount() {
    const count = await this.getCommentCount({
      targetId: this.item.id,
      projectScoped: false,
      onlyGet: true
    })
    if (typeof count !== "number") return

    this.commentsResponse = new ServerResponse({
      ...this.commentsResponse,
      totalElements: count
    })
  }

  async loadFiles() {
    this.$wait.start("getFiles")
    try {
      const response = await this.getFiles({
        resourceType: "TODO_ITEM",
        targetId: this.item.id,
        projectScoped: false,
        filter: this.filterFiles
      })
      if (!response) return

      this.filesResponse = new ServerResponse({
        ...response,
        content: parsingServerResponseContent({
          originalContent: this.filesResponse.content,
          serverResponseContent: response.content,
          numberOfPage: response.pageable.pageNumber
        })
      })
    } finally {
      this.$wait.end("getFiles")
    }
  }

  /** Uploads and their progress are shown by FileList from the file store; here the list is only refreshed. */
  async onUploadFiles(files: VUFile[]) {
    if (!files.length) return

    const uploaded = await this.uploadFiles({
      attachments: files.map(file => file.file),
      resourceType: "TODO_ITEM",
      targetId: this.item.id,
      projectScoped: false,
      uploadListKey: this.item.id
    })
    if (!uploaded) return

    this.upsertFiles(uploaded)
  }

  async onDeleteFile(file: FileMetaData) {
    const deleted = await this.deleteAttachment({
      todoItemId: this.item.id,
      fileMetadataId: file.id
    })
    if (!deleted) return

    this.filesResponse.content = this.filesResponse.content.filter(item => item.id !== file.id)
    this.filesResponse.totalElements = Math.max(0, this.filesResponse.totalElements - 1)
    this.filesResponse.numberOfElements = Math.max(0, this.filesResponse.numberOfElements - 1)
  }

  onScrollFiles() {
    if (this.filesResponse.last || this.$wait.is("getFiles")) return

    this.filterFiles.page++
    this.loadFiles()
  }

  onShowDeletedFiles(value: boolean) {
    this.filterFiles.withDeleted = value
    this.filterFiles.page = 1
    this.loadFiles()
  }

  /** Applies ToDo comment events and adds their attachments from the SSE payload. */
  @Watch("subscriptionComments", { immediate: true, deep: true })
  onSubscriptionCommentsChange() {
    const comments = this.subscriptionComments?.filter(
      event => event.targetId === this.item.id && event.targetType === "TODO_ITEM"
    )
    if (!comments?.length) return

    const response = new ServerResponse({
      ...this.commentsResponse,
      content: this.commentsResponse.content.map(comment => Comment.clone(comment))
    })
    comments.forEach(({ comment: commentData, changeType }) => {
      const comment = Comment.create(commentData)
      const incomingChangeId = comment.feContext?.changeId
      const index = response.content.findIndex(item => item.id === comment.id)
      const existsByChangeId =
        !!incomingChangeId &&
        response.content.some(item => item.feContext?.changeId === incomingChangeId)

      if (changeType === "CREATED" && index === -1 && !existsByChangeId) {
        response.content.unshift(comment)
        response.totalElements++
        response.numberOfElements++
      } else if (changeType === "UPDATED" && index !== -1) {
        response.content.splice(index, 1, comment)
      } else if (changeType === "DELETED" && index !== -1) {
        response.content.splice(index, 1)
        response.totalElements = Math.max(0, response.totalElements - 1)
        response.numberOfElements = Math.max(0, response.numberOfElements - 1)
      }
    })
    this.commentsResponse = response
    this.upsertFiles(comments.flatMap(event => event.comment.fileMetadata ?? []))
  }

  /** Applies attachment metadata changes only to files already belonging to the open ToDo. */
  @Watch("subscriptionFiles")
  onSubscriptionFilesChange(notification: ServerSendAttachmentUpdate | null) {
    if (!notification?.attachmentsChanged) return

    const fileIndex = this.filesResponse.content.findIndex(
      file => file.id === notification.attachmentsChanged.id
    )
    if (fileIndex === -1) return

    if (notification.changeType === "DELETED" && !this.filterFiles.withDeleted) {
      this.filesResponse.content.splice(fileIndex, 1)
      this.filesResponse.totalElements = Math.max(0, this.filesResponse.totalElements - 1)
      this.filesResponse.numberOfElements = Math.max(0, this.filesResponse.numberOfElements - 1)
      return
    }

    const currentFile = this.filesResponse.content[fileIndex]
    this.filesResponse.content.splice(
      fileIndex,
      1,
      FileMetaData.create({
        ...currentFile,
        ...notification.attachmentsChanged,
        deleted: notification.changeType === "DELETED" || notification.attachmentsChanged.deleted
      })
    )
  }

  /** Adds new SSE files and replaces existing entries without requesting the list again. */
  upsertFiles(files: FileMetaData[]) {
    files.forEach(fileData => {
      const file = FileMetaData.create(fileData)
      const fileIndex = this.filesResponse.content.findIndex(item => item.id === file.id)

      if (fileIndex !== -1) {
        this.filesResponse.content.splice(fileIndex, 1, file)
        return
      }
      if (file.deleted && !this.filterFiles.withDeleted) return

      this.filesResponse.content.unshift(file)
      this.filesResponse.totalElements++
      this.filesResponse.numberOfElements++
      this.filesResponse.empty = false
    })
  }

  onUpdateCommentFilter(filter: FilterComment) {
    this.filterForComments = filter
  }

  @Emit("back")
  onBack() {
    return
  }

  @Emit("edit")
  onEdit() {
    return this.item
  }

  @Emit()
  onCheckboxClick(payload) {
    return payload
  }
}
</script>
